import ICAL from 'ical.js';
import { TimeSlot } from '../types/timetable';
import { StorageService } from './StorageService';
import { scheduleClassNotifications } from './notifications';
import { useTimetableStore } from '../store/timetableStore';

// Define a set of vibrant colors for classes
const CLASS_COLORS = [
  '#4CAF50',  // Green
  '#2196F3',  // Blue
  '#9C27B0',  // Purple
  '#FF9800',  // Orange
  '#E91E63',  // Pink
  '#00BCD4',  // Cyan
  '#3F51B5',  // Indigo
  '#FF5722',  // Deep Orange
  '#009688',  // Teal
  '#673AB7',  // Deep Purple
  '#FFC107',  // Amber
  '#8BC34A',  // Light Green
  '#03A9F4',  // Light Blue
  '#F44336',  // Red
  '#795548',  // Brown
];

// Get a random color from the array
function getRandomColor(): string {
  return CLASS_COLORS[Math.floor(Math.random() * CLASS_COLORS.length)];
}

// Create a map to consistently assign colors to subjects
const subjectColorMap = new Map<string, string>();

// Get or create a color for a subject
function getColorForSubject(subject: string): string {
  if (!subjectColorMap.has(subject)) {
    // Assign a new random color
    subjectColorMap.set(subject, getRandomColor());
  }
  return subjectColorMap.get(subject)!;
}

export async function importTimetableFromICS(icsContent: string): Promise<void> {
  try {
    // Clear the subject color map for new import
    subjectColorMap.clear();
    
    // Parse ICS content
    const jcalData = ICAL.parse(icsContent);
    const comp = new ICAL.Component(jcalData);
    const events = comp.getAllSubcomponents('vevent');

    // Initialize timetable data structure
    const timetable: { [key: string]: TimeSlot[] } = {
      'Mon': [],
      'Tue': [],
      'Wed': [],
      'Thu': [],
      'Fri': [],
    };

    // Process each event
    events.forEach(event => {
      const vevent = new ICAL.Event(event);
      const startDate = vevent.startDate.toJSDate();
      const endDate = vevent.endDate.toJSDate();
      const dayOfWeek = startDate.toLocaleString('en-US', { weekday: 'long' });
      
      // Map full day names to three-letter codes
      const dayMap: { [key: string]: string } = {
        'Monday': 'Mon',
        'Tuesday': 'Tue',
        'Wednesday': 'Wed',
        'Thursday': 'Thu',
        'Friday': 'Fri'
      };
      
      const shortDay = dayMap[dayOfWeek];
      if (shortDay) {
        // Format times in 24-hour format
        const startTime = startDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
        const endTime = endDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
        
        // Extract staff name from description (format: "Staff: NAME")
        const staffMatch = (vevent.description || '').match(/Staff: (.+)/);
        const staffName = staffMatch ? staffMatch[1] : 'TBA';

        const subject = vevent.summary || 'Unknown Module';
        
        // Process location to remove "Room" prefix if it exists
        const location = vevent.location ? vevent.location.replace(/^Room\s+/i, '') : 'TBA';
        
        const classData: TimeSlot = {
          id: vevent.uid || Math.random().toString(36).substr(2, 9),
          subject: subject,
          time: `${startTime} - ${endTime}`,
          location: location,
          day: shortDay,
          staff: staffName,
          color: getColorForSubject(subject), // Assign consistent color per subject
        };

        timetable[shortDay].push(classData);

        // Schedule notification for this class
        try {
          scheduleClassNotifications(classData);
        } catch (error) {
          console.error('Error scheduling notification:', error);
        }
      }
    });

    // Sort classes by start time for each day
    Object.keys(timetable).forEach(day => {
      timetable[day].sort((a, b) => {
        const aStart = a.time.split(' - ')[0];
        const bStart = b.time.split(' - ')[0];
        return aStart.localeCompare(bStart);
      });
    });

    // Save timetable to storage
    await StorageService.saveTimetable(timetable);
    
    // Update the timetable store
    const store = useTimetableStore.getState();
    await store.initialize();
    
    console.log('Timetable imported successfully');
  } catch (error) {
    console.error('Error importing timetable:', error);
    throw error;
  }
}
